import React, { useEffect, useState, useCallback } from 'react';
import './App.css';
import 'react-toastify/dist/ReactToastify.css';
import { ToastContainer, toast } from 'react-toastify';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import Dashboard from './components/Dashboard';
import PlanoInteractivo from './components/PlanoInteractivo';
import ListaLotes from './components/ListaLotes';
import FormularioLote from './components/FormularioLote';
import Perfil from './components/Perfil';
import Login from './components/Login';
import Register from './components/Register';
import Landing from './components/Landing';
import Documentacion from './components/Documentacion';
import Soporte from './components/Soporte';
import PreguntasFrecuentes from './components/PreguntasFrecuentes';
import Terminos from './components/Terminos';
import PoliticaPrivacidad from './components/PoliticaPrivacidad';
import ConfirmModal from './components/ConfirmModal';
import Verify from './components/Verify';
import { loteService } from './services/api';
import { authService } from './services/auth';

/**
 * Layout principal para rutas autenticadas
 */
function PrivateDashboardLayout({
  loteSeleccionado,
  setLoteSeleccionado,
  refreshTrigger,
  setRefreshTrigger,
  currentPlano,
  setCurrentPlano,
  confirmModal,
  onRequestDeleteLote,
  onConfirmDeleteLote,
  onCancelDelete,
  onLoteClick,
  onEditLote,
  onSaveLote,
  onCancelForm
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const [menuAbierto, setMenuAbierto] = useState(false);
  const user = authService.getUser();

  const handleLogout = () => {
    authService.clearSession();
    window.dispatchEvent(new Event('logout'));
    navigate('/landing', { replace: true });
    toast.info('Sesión cerrada');
  };

  const toggleMenu = () => {
    setMenuAbierto(!menuAbierto);
  };

  const handleNavigate = (path) => {
    navigate(path);
    if (window.innerWidth <= 768) {
      setMenuAbierto(false);
    }
  };

  const getCurrentPage = () => {
    const pathname = location.pathname;
    if (pathname.includes('/app/dashboard') || pathname === '/app') return 'dashboard';
    if (pathname.includes('/app/plano')) return 'plano';
    if (pathname.includes('/app/lista') || pathname.includes('/app/formulario')) return 'lista';
    if (pathname.includes('/app/perfil')) return 'perfil';
    return 'dashboard';
  };

  // Wrapper para onEditLote que navega al formulario
  const handleEditLoteWrapper = (lote) => {
    onEditLote(lote);
    navigate('/app/formulario');
  };

  // Wrapper para onSaveLote que navega a lista después de guardar
  const handleSaveLoteWrapper = async (loteData) => {
    await onSaveLote(loteData);
    navigate('/app/lista');
  };

  // Wrapper para onCancelForm que navega al dashboard o lista
  const handleCancelFormWrapper = () => {
    onCancelForm();
    // Navegar a lista de lotes (o dashboard si prefieres)
    navigate('/app/lista');
  };

  // Memoizar callbacks para evitar re-renders innecesarios
  const handleOnPlanoChange = useCallback((plano) => {
    setCurrentPlano(plano);
  }, [setCurrentPlano]);

  const handleOnLoteActualizado = useCallback(() => {
    setRefreshTrigger((prev) => prev + 1);
  }, [setRefreshTrigger]);

  // Determinar qué componente renderizar
  const renderContent = () => {
    if (location.pathname === '/app/plano') {
      return (
        <PlanoInteractivo
          onLoteClick={onLoteClick}
          onLoteActualizado={handleOnLoteActualizado}
          onPlanoChange={handleOnPlanoChange}
        />
      );
    }

    if (location.pathname === '/app/lista') {
      return (
        <ListaLotes
          onEditLote={handleEditLoteWrapper}
          onRequestDelete={onRequestDeleteLote}
          onDeleteLote={onRequestDeleteLote}
          refreshTrigger={refreshTrigger}
        />
      );
    }

    if (location.pathname === '/app/formulario') {
      return (
        <FormularioLote
          lote={loteSeleccionado}
          onSave={handleSaveLoteWrapper}
          onCancel={handleCancelFormWrapper}
          currentPlano={currentPlano}
        />
      );
    }

    if (location.pathname === '/app/perfil') {
      return <Perfil />;
    }

    // Por defecto Dashboard
    return <Dashboard />;
  };

  return (
    <div className="App">
      <ToastContainer position="top-right" />
      <button
        className={`menu-hamburguesa ${menuAbierto ? 'abierto' : ''}`}
        onClick={toggleMenu}
        aria-label="Toggle menu"
      >
        <span></span>
        <span></span>
        <span></span>
      </button>

      {menuAbierto && <div className="menu-overlay" onClick={() => setMenuAbierto(false)}></div>}

      <aside className={`sidebar ${menuAbierto ? 'abierto' : ''}`}>
        <div className="sidebar-header">
          <h1 className="app-title">LOTEA</h1>
          <span className="app-subtitle">Sistema de Gestión de Lotes</span>
        </div>

        <nav className="sidebar-nav">
          <button
            onClick={() => handleNavigate('/app/dashboard')}
            className={getCurrentPage() === 'dashboard' ? 'active' : ''}
          >
            📊 Dashboard
          </button>
          <button
            onClick={() => handleNavigate('/app/plano')}
            className={getCurrentPage() === 'plano' ? 'active' : ''}
          >
            🗺️ Plano
          </button>
          <button
            onClick={() => handleNavigate('/app/lista')}
            className={getCurrentPage() === 'lista' ? 'active' : ''}
          >
            📋 Lista
          </button>
        </nav>

        <div className="sidebar-actions">
          <button
            onClick={() => {
              setLoteSeleccionado(null);
              handleNavigate('/app/formulario');
            }}
            className="btn-nuevo"
          >
            ➕ Nuevo Lote
          </button>
        </div>

        <div className="sidebar-footer">
          <button
            onClick={() => handleNavigate('/app/perfil')}
            className="sidebar-user-btn"
          >
            👤 {user?.name || 'Usuario'}
          </button>
          <button onClick={handleLogout} className="btn-logout">
            🔒 Cerrar sesión
          </button>
        </div>
      </aside>

      <div className="app-content-wrapper">
        <main className="app-main">
          {renderContent()}

          {confirmModal.visible && (
            <ConfirmModal
              visible={confirmModal.visible}
              title="Confirmar eliminación"
              message={`¿Estás seguro de que deseas eliminar el lote ${confirmModal.lote?.numero || ''}?`}
              onConfirm={onConfirmDeleteLote}
              onCancel={onCancelDelete}
            />
          )}
        </main>

        <footer className="app-footer">
          <p>© 2026 Sistema de Gestión de Lotes Inmobiliarios</p>
        </footer>
      </div>
    </div>
  );
}

/**
 * Componente de rutas públicas
 */
function PublicRoutes() {
  const navigate = useNavigate();

  useEffect(() => {
    const handleStorageChange = () => {
      const isAuth = authService.getUser() !== null;
      if (isAuth) {
        navigate('/app/dashboard', { replace: true });
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [navigate]);

  const handleAuthSuccess = (userData) => {
    setTimeout(() => {
      navigate('/app/dashboard', { replace: true });
    }, 100);
  };

  return (
    <div className="App public-app">
      <ToastContainer position="top-right" />
      <main className="public-main">
        <Routes>
          <Route
            path="/landing"
            element={
              <Landing
                onLoginClick={() => navigate('/login')}
                onRegisterClick={() => navigate('/register')}
                onNavigate={(v) => {
                  const routeMap = {
                    'documentacion': '/documentacion',
                    'soporte': '/soporte',
                    'faq': '/faq',
                    'terminos': '/terminos',
                    'privacidad': '/privacidad',
                  };
                  navigate(routeMap[v] || '/landing');
                }}
              />
            }
          />
          <Route
            path="/login"
            element={
              <Login
                onSuccess={handleAuthSuccess}
                onRegisterClick={() => navigate('/register')}
                onBackToLanding={() => navigate('/landing')}
              />
            }
          />
          <Route
            path="/register"
            element={
              <Register
                onSuccess={handleAuthSuccess}
                onLoginClick={() => navigate('/login')}
                onBackToLanding={() => navigate('/landing')}
              />
            }
          />
          <Route
            path="/verify"
            element={<Verify onBackToLanding={() => navigate('/landing')} />}
          />
          <Route
            path="/documentacion"
            element={<Documentacion onBack={() => navigate('/landing')} />}
          />
          <Route
            path="/soporte"
            element={<Soporte onBack={() => navigate('/landing')} />}
          />
          <Route
            path="/faq"
            element={<PreguntasFrecuentes onBack={() => navigate('/landing')} />}
          />
          <Route
            path="/terminos"
            element={<Terminos onBack={() => navigate('/landing')} />}
          />
          <Route
            path="/privacidad"
            element={<PoliticaPrivacidad onBack={() => navigate('/landing')} />}
          />
          <Route path="*" element={<Navigate to="/landing" replace />} />
        </Routes>
      </main>
    </div>
  );
}

/**
 * Componente principal de la aplicación
 */
function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(authService.getUser() !== null);
  const [loteSeleccionado, setLoteSeleccionado] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [currentPlano, setCurrentPlano] = useState(null);
  const [confirmModal, setConfirmModal] = useState({ visible: false, lote: null });

  useEffect(() => {
    const handleStorageChange = () => {
      setIsAuthenticated(authService.getUser() !== null);
    };

    const handleLogout = () => {
      setIsAuthenticated(false);
    };

    const handleLogin = () => {
      setIsAuthenticated(true);
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('logout', handleLogout);
    window.addEventListener('login', handleLogin);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('logout', handleLogout);
      window.removeEventListener('login', handleLogin);
    };
  }, []);

  const requestDeleteLote = (lote) => {
    setConfirmModal({ visible: true, lote });
  };

  const confirmDeleteLote = async () => {
    const id = confirmModal.lote?._id;
    try {
      await loteService.eliminarLote(id);
      toast.success('Lote eliminado correctamente');
      setRefreshTrigger((prev) => prev + 1);
    } catch (error) {
      toast.error('Error al eliminar el lote: ' + (error.message || 'Error desconocido'));
    } finally {
      setConfirmModal({ visible: false, lote: null });
    }
  };

  const cancelDelete = () => {
    setConfirmModal({ visible: false, lote: null });
  };

  const handleLoteClick = (lote) => {
    setLoteSeleccionado(lote);
  };

  const handleEditLote = (lote) => {
    setLoteSeleccionado(lote);
  };

  const handleSaveLote = async (loteData) => {
    try {
      if (loteSeleccionado) {
        await loteService.actualizarLote(loteSeleccionado._id, loteData);
        toast.success('Lote actualizado correctamente');
      } else {
        if (!loteData.plano && currentPlano && currentPlano._id) {
          loteData.plano = currentPlano._id;
        }
        await loteService.crearLote(loteData);
        toast.success('Lote creado correctamente');
      }
      setRefreshTrigger((prev) => prev + 1);
      setLoteSeleccionado(null);
    } catch (error) {
      toast.error('Error al guardar el lote: ' + error.message);
    }
  };

  const handleCancelForm = () => {
    setLoteSeleccionado(null);
  };

  return (
    <Router>
      <Routes>
        <Route
          path="/"
          element={
            isAuthenticated ? (
              <Navigate to="/app/dashboard" replace />
            ) : (
              <Navigate to="/landing" replace />
            )
          }
        />
        <Route
          path="/app/*"
          element={
            isAuthenticated ? (
              <PrivateDashboardLayout
                loteSeleccionado={loteSeleccionado}
                setLoteSeleccionado={setLoteSeleccionado}
                refreshTrigger={refreshTrigger}
                setRefreshTrigger={setRefreshTrigger}
                currentPlano={currentPlano}
                setCurrentPlano={setCurrentPlano}
                confirmModal={confirmModal}
                onRequestDeleteLote={requestDeleteLote}
                onConfirmDeleteLote={confirmDeleteLote}
                onCancelDelete={cancelDelete}
                onLoteClick={handleLoteClick}
                onEditLote={handleEditLote}
                onSaveLote={handleSaveLote}
                onCancelForm={handleCancelForm}
              />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route path="/*" element={<PublicRoutes />} />
      </Routes>
    </Router>
  );
}

export default App;
